/**
 * Product-family grouping - ONE definition, shared by every screen that has to show "a product"
 * rather than "a SKU".
 *
 * A row in `products` is a VARIANT (AminOPF 250 ml, AminOPF 500 ml, AminOPF 1 L). What a user calls
 * a product is the FAMILY those variants belong to: `group_code`. A screen that lists families and
 * a chart on the same screen that lists variants disagree about both the NAME and the NUMBER, which
 * is what these helpers exist to prevent.
 */

export interface VariantLike {
  sku?: string | null;
  groupCode?: string | null;
  name?: string | null;
  variantLabel?: string | null;
}

/** Stable family key. No `group_code` (a one-off SKU) -> the variant is its own family. */
export function productGroupKey(v: VariantLike): string {
  return v.groupCode ? String(v.groupCode) : `__${v.sku ?? v.name ?? ''}`;
}

/**
 * Family name for display: the variant name with its pack suffix removed ("AminOPF 500 ml" ->
 * "AminOPF"). Plain suffix matching, not a regex - the label is hand-typed and a pack like "4+" or
 * "0.5L" carries characters a pattern would have to escape.
 */
export function productGroupName(v: VariantLike): string {
  const name = String(v.name ?? '').trim();
  const label = String(v.variantLabel ?? '').trim();
  if (!label) return name;
  if (!name.toLowerCase().endsWith(label.toLowerCase())) return name;
  return name.slice(0, name.length - label.length).trim() || name;
}

/**
 * Disambiguate family labels that collide. Two `group_code`s can render the same name (a family
 * whose variants are named inconsistently takes the name of its largest variant), and two identical
 * bars on one chart are unreadable - the group code is the identity that actually differs, so it is
 * appended to the ones that clash. Untouched when every name is already unique.
 */
export function disambiguateGroupNames<T extends { key: string; product: string }>(rows: T[]): T[] {
  const seen = new Map<string, number>();
  for (const r of rows) seen.set(r.product, (seen.get(r.product) ?? 0) + 1);
  return rows.map((r) => (
    (seen.get(r.product) ?? 0) > 1 && r.key && r.key !== r.product && !r.key.startsWith('__')
      ? { ...r, product: `${r.product} · ${r.key}` }
      : r
  ));
}
